import {useEffect} from 'react'

import Loading from '../../common/components/List/Loading.js'
import {
  stocktakeDetailQuerySelector,
  refreshStocktakeLines,
  skuListSelector,
  setSelectedSKUs,
  indeterminateSelectedSelector,
  allSelectedSelector,
  isFilteredSelector,
  countSelector,
  setLimit,
  setOffset,
  selectStocktakeLine,
  stocktakeDetailFormSelector,
  selectedSKUsSelector,
  isLoadingListSelector,
  stocktakeDetailReferenceIDSelector,
  getCurrentDetailStocktake,
  expandModeSelector,
  setExpandMode,
  setExpandedSKUs,
  expandedSKUsSelector,
  isExpandedSelector,
  stocktakeDetailToggleExpanded,
} from './stocktakeDetailFunctions.js'
import {getState, useSelector} from '../../store.js'
import ButtonLink from '../../common/components/Button/ButtonLink.js'
import Quantity from '../../common/components/Quantity.js'
import {getProductName} from '../../data/products.js'
import Footer from '../../common/components/List/Footer/index.js'
import Checkbox from '../../common/components/Checkbox.js'
import PopoverImage from '../../common/components/PopoverImage.js'
import {showImgsStocktakeSelector} from '../../data/company.js'
import SelectAllButton from '../../common/components/List/SelectAllButton.js'
import {showConfirmMessageModal} from '../../common/components/Modal/ConfirmMessageModal.js'
import {
  Count,
  plural,
  Plural,
  PluralBlock,
} from '../../common/components/Plural.js'
import apiverson from '../../common/apiverson.js'
import {showMessageToast} from '../Header/Toast/index.js'
import {buildPath} from '../../common/querystring.js'
import {stocktakeSelector} from '../../data/stocktakes.js'
import TextArea from '../../common/components/TextArea.js'
import ExpandAllButton, {
  EXPAND_MODE_COLLAPSED,
  EXPAND_MODE_EXPANDED,
} from '../../common/components/List/ExpandAllButton.js'
import Icon from '../../common/icons/Icon.js'

async function onStocktakeRemoveLine(criteria, count, {outcome: willDelete}) {
  if (!willDelete) {
    return
  }

  const referenceID = stocktakeDetailReferenceIDSelector(getState())

  try {
    await apiverson.delete(buildPath(['stocktake', referenceID, 'line']), {
      criteria,
    })

    showMessageToast(plural(count)`Removed Product${'s'}`)

    await getCurrentDetailStocktake()

    await refreshStocktakeLines()
  } catch (err) {
    throw new Error(
      `Error removing products from stocktake: ${err.error_message || err.message}`,
    )
  }
}

async function removeStocktakeLine(criteria, count) {
  await showConfirmMessageModal({
    title: plural(count)`Remove Product${['s']}`,
    Message: () => (
      <PluralBlock count={count}>
        Do you want to remove <Count /> <Plural word="product" /> from the
        stocktake?
      </PluralBlock>
    ),
    onResolve: (...args) => onStocktakeRemoveLine(criteria, count, ...args),
  })
}

async function editStocktakeLineNotes(lineID, internal_notes) {
  await showConfirmMessageModal({
    title: 'Edit Line Notes',
    Message: ({form, updateModalForm}) => (
      <ul className="list list--no-style">
        <li>
          <TextArea
            label="Explanation"
            rows="10"
            id="comment"
            value={form.confirmValue}
            onChange={(value) => updateModalForm({confirmValue: value})}
            autoFocus
          />
        </li>
      </ul>
    ),
    confirmText: 'Save',
    cancelText: 'Cancel',
    confirmValue: internal_notes || '',
    onResolve: async ({outcome}) => {
      if (outcome === false) {
        return
      }

      const referenceID = stocktakeDetailReferenceIDSelector(getState())

      try {
        await apiverson.put(
          buildPath(['stocktake', referenceID, 'line', lineID]),
          {
            internal_notes: outcome,
          },
        )

        showMessageToast('Updated stocktake line notes')

        await getCurrentDetailStocktake()

        await refreshStocktakeLines()
      } catch (err) {
        throw new Error(
          `Error updating stocktake line notes: ${err.error_message || err.message}`,
        )
      }
    },
  })
}

export function StocktakeDetailExpandAll() {
  const expandMode = useSelector(expandModeSelector)
  const expandedSKUCount = useSelector(expandedSKUsSelector).length

  const shouldCollapse =
    expandedSKUCount > 1 || expandMode === EXPAND_MODE_EXPANDED

  return (
    <li
      className="inline-block margin-right-3 tooltip--top"
      data-hint={shouldCollapse ? 'Collapse all SKUs' : 'Expand all SKUs'}
    >
      <ExpandAllButton
        className="meta-rmalistheader-button-expandall"
        shouldCollapse={shouldCollapse}
        onExpandAll={() => {
          setExpandedSKUs([])
          setExpandMode(EXPAND_MODE_EXPANDED)
        }}
        onCollapseAll={() => {
          setExpandedSKUs([])
          setExpandMode(EXPAND_MODE_COLLAPSED)
        }}
      />
    </li>
  )
}

function Row({sku, stocktake}) {
  const {lineMap} = useSelector(stocktakeDetailFormSelector)
  const selectedSKUS = useSelector(selectedSKUsSelector)
  const showImgsStocktake = useSelector(showImgsStocktakeSelector)
  const isExpanded = useSelector((state) => isExpandedSelector(state, {sku}))

  const line = lineMap.get(sku)
  const isSelected = selectedSKUS.includes(sku)

  if (!line) {
    return null
  }

  const {product} = line
  const productName = getProductName(product)
  const warehouse = product.warehouses.find(
    ({id}) => id === stocktake.warehouse_id,
  )

  return (
    <tr className="table__tr table__tr--mo-row">
      <td className="table__td table__td--order-checkbox relative">
        <Checkbox
          checked={isSelected}
          onClick={(checked, event) =>
            selectStocktakeLine(sku, checked, event.shiftKey)
          }
        />
      </td>
      <td className="table__td">
        <div className="flex">
          {showImgsStocktake && (
            <PopoverImage src={product.image_url} alt={productName} />
          )}
          <dl className="list">
            <dt className="list__title fs-00 lh-md line-detail margin-bottom-0">
              <a
                className="btn--link darker"
                href={`#/product/${encodeURIComponent(sku)}`}
              >
                <strong className="block margin-bottom-3">{productName}</strong>
              </a>
              <div className="block unbold margin-bottom-3">SKU: {sku}</div>
            </dt>
            {isExpanded && (
              <>
                {product.upc && (
                  <dd className="list__item fs-00 lh-md">
                    <span className="block unbold margin-bottom-3">
                      UPC: {product.upc}
                    </span>
                  </dd>
                )}
                {warehouse.location_in_warehouse && (
                  <dd className="list__item fs-00 lh-md">
                    <span className="block unbold margin-bottom-3">
                      LOC: {warehouse.location_in_warehouse}
                    </span>
                  </dd>
                )}
                <dd className="list__item divider--top">
                  <dl className="list--order-data">
                    <dt className="list__title--order-data">Notes</dt>
                    {line.internal_notes ? (
                      <dd className="list__item--order-data">
                        <div className="ws-pre-wrap fs-n0 lh-md">
                          {line.internal_notes}
                        </div>
                        <ButtonLink
                          className="fs-n0"
                          onClick={() =>
                            editStocktakeLineNotes(line.id, line.internal_notes)
                          }
                        >
                          Edit
                        </ButtonLink>
                      </dd>
                    ) : (
                      <dd className="list__item--order-data">
                        <ButtonLink
                          className="fs-n0"
                          onClick={() => editStocktakeLineNotes(line.id)}
                        >
                          Add
                        </ButtonLink>
                      </dd>
                    )}
                  </dl>
                </dd>
              </>
            )}
          </dl>
        </div>
      </td>
      <td className="table__td">
        {line.counted_quantity === null ? (
          <em className="fs-00 text--lt-grey">Uncounted</em>
        ) : line.reconciled_date !== null ? (
          <span className="fs-00 lh-md">Reconciled</span>
        ) : (
          <span className="fs-00 lh-md">Counted</span>
        )}
      </td>
      <td className="table__td align-right">
        <span className="fs-01 lh-md">
          <Quantity value={line.expected_quantity} options={{ifNull: '–'}} />
        </span>
      </td>
      <td className="table__td align-right">
        <strong className="fs-01 lh-md">
          <Quantity value={line.counted_quantity} options={{ifNull: '–'}} />
        </strong>
      </td>
      <td className="table__td align-right">
        <div>
          <ButtonLink
            className="no-underline"
            onClick={() => removeStocktakeLine({sku: [sku]}, 1)}
          >
            <Icon name="trash" className="fs-01 lh-0 v-align-middle" />
          </ButtonLink>
        </div>
        <ButtonLink
          className="no-underline btn--expand-product-row margin-top-5"
          onClick={() => stocktakeDetailToggleExpanded(sku)}
        >
          <Icon name="expand" className="fs-01 lh-0 v-align-middle" />
        </ButtonLink>
      </td>
    </tr>
  )
}

function EmptyList({colSpan}) {
  const isFiltered = useSelector(isFilteredSelector)

  return (
    <tr className="table__tr">
      <td className="table__td" colSpan={colSpan}>
        <div className="padding-top-25 padding-bottom-25 align-center">
          <strong className="text--md-grey fs-02 lh-md">
            {isFiltered
              ? 'No Products match the current search terms.'
              : 'There aren’t any Products added to this Stocktake.'}
          </strong>
        </div>
      </td>
    </tr>
  )
}

function Table() {
  const skuList = useSelector(skuListSelector)
  const selectedSKUs = useSelector(selectedSKUsSelector)
  const indeterminate = useSelector(indeterminateSelectedSelector)
  const checked = useSelector(allSelectedSelector)
  const referenceID = useSelector(stocktakeDetailReferenceIDSelector)
  const stocktake = useSelector((state) =>
    stocktakeSelector(state, {referenceID}),
  )

  return (
    <table className="table table--zebra-list table--product-list">
      <thead>
        <tr className="table__tr">
          <th className="table__th table__th--sm v-align-middle no-border table__th--order-checkbox">
            <SelectAllButton
              checked={checked}
              indeterminate={indeterminate}
              onChange={(value) => setSelectedSKUs(value ? skuList : [])}
            />
          </th>
          <th className="table__th th-width-30 v-align-middle no-border">
            <span className="fs-00 lh-md">Product</span>
          </th>
          <th className="table__th th-width-20 v-align-middle no-border">
            <span className="fs-00 lh-md">Status</span>
          </th>
          <th className="table__th th-width-20 align-right v-align-middle no-border">
            <span className="fs-00 lh-md">Expected Qty</span>
          </th>
          <th className="table__th table__th--sm th-width-20 align-right v-align-middle no-border">
            <span className="fs-00 lh-md">Counted Qty</span>
          </th>
          <th className="table__th table__th--sm v-align-middle no-border align-right">
            {selectedSKUs.length > 0 && (
              <ButtonLink
                className="no-underline"
                onClick={() =>
                  removeStocktakeLine({sku: selectedSKUs}, selectedSKUs.length)
                }
              >
                <Icon name="trash" className="fs-01 lh-0 v-align-middle" />
              </ButtonLink>
            )}
          </th>
        </tr>
      </thead>
      <tbody className="table__tbody--full-border">
        {skuList.map((sku) => (
          <Row key={sku} sku={sku} stocktake={stocktake} />
        ))}
        {skuList.length === 0 && <EmptyList colSpan="6" />}
      </tbody>
    </table>
  )
}

export default function List() {
  const params = useSelector(stocktakeDetailQuerySelector)
  const isLoadingList = useSelector(isLoadingListSelector)
  const count = useSelector(countSelector)

  useEffect(() => {
    refreshStocktakeLines()
  }, [params])

  return (
    <>
      {isLoadingList ? (
        <div className="medium-12 columns list--empty-content margin-top-40 margin-bottom-40">
          <Loading />
        </div>
      ) : (
        <>
          <Table />
          <Footer
            isLoading={isLoadingList}
            limit={params.limit}
            offset={params.offset}
            count={count}
            setLimit={setLimit}
            setOffset={setOffset}
            preventResetOffset
          />
        </>
      )}
    </>
  )
}
